import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, List, ListItemButton, ListItemIcon, ListItemText, Typography,
  Avatar, IconButton, Chip, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import { conectarSocketAdmin } from '../../utils/socket';
import adminAxios from '../../utils/adminAxios';
import { logoutAdmin } from '../../actions/adminAuth';
import AdminNotifications from './AdminNotifications';

const ANCHO = 256;

// Secciones del menú según el diseño.
const secciones = [
  {
    titulo: 'Resumen',
    items: [{ to: '/admin', end: true, label: 'Dashboard', icon: <GridViewOutlinedIcon /> }],
  },
  {
    titulo: 'Verificación',
    items: [{ to: '/admin/verificacion', label: 'Verificación de Usuarios', icon: <VerifiedUserOutlinedIcon />, badge: 'verificacion' }],
  },
  {
    titulo: 'Directorio',
    items: [
      { to: '/admin/negocios', label: 'Grupos o Negocios', icon: <StorefrontOutlinedIcon /> },
      { to: '/admin/paginas', label: 'Páginas de Servicios', icon: <LayersOutlinedIcon /> },
      { to: '/admin/servicios', label: 'Servicios', icon: <BoltOutlinedIcon /> },
      { to: '/admin/personas', label: 'Personas', icon: <PeopleAltOutlinedIcon /> },
    ],
  },
  {
    titulo: 'Catálogo',
    items: [{ to: '/admin/categorias', label: 'Categorías y Subcategorías', icon: <SellOutlinedIcon /> }],
  },
  {
    titulo: 'Soporte',
    items: [
      { to: '/admin/tickets', label: 'Gestión de Tickets', icon: <HeadsetMicOutlinedIcon />, badge: 'tickets' },
      { to: '/admin/soporte', label: 'Chat en vivo', icon: <ChatBubbleOutlineOutlinedIcon />, badge: 'soporte' },
    ],
  },
  {
    titulo: 'Finanzas',
    items: [
      { to: '/admin/finanzas', label: 'Centro Financiero', icon: <AccountBalanceWalletOutlinedIcon /> },
      { to: '/admin/promociones', label: 'Códigos Promocionales', icon: <LocalOfferOutlinedIcon /> },
      { to: '/admin/facturacion', label: 'Facturación', icon: <ReceiptLongOutlinedIcon /> },
    ],
  },
  {
    titulo: 'Sistema',
    items: [{ to: '/admin/configuracion', label: 'Configuración Global', icon: <SettingsOutlinedIcon /> }],
  },
];

// Título/subtítulo de la barra superior según la ruta.
const metaPorRuta = (pathname, nombre) => {
  if (pathname === '/admin') return { titulo: 'Dashboard', sub: `Bienvenido de nuevo, ${nombre}` };
  if (pathname.startsWith('/admin/verificacion')) return { titulo: 'Verificación de Usuarios', sub: 'Revisa y aprueba documentos' };
  if (pathname.startsWith('/admin/negocios') || pathname.startsWith('/admin/paginas') || pathname.startsWith('/admin/servicios'))
    return { titulo: 'Directorio de Entidades', sub: 'Gestiona todos los negocios y servicios de la plataforma' };
  if (pathname.startsWith('/admin/personas')) return { titulo: 'Personas', sub: 'Uso del chat, silencios y reportes de las personas de la plataforma' };
  if (pathname.startsWith('/admin/categorias')) return { titulo: 'Catálogo de Servicios', sub: 'Gestiona las categorías y subcategorías del marketplace' };
  if (pathname.startsWith('/admin/tickets')) return { titulo: 'Gestión de Tickets', sub: 'Administra solicitudes de soporte, reportes y aclaraciones' };
  if (pathname.startsWith('/admin/finanzas')) return { titulo: 'Centro Financiero', sub: 'Custodia, comisiones, dispersiones y devoluciones del marketplace' };
  if (pathname.startsWith('/admin/promociones')) return { titulo: 'Códigos Promocionales', sub: 'Crea y administra los descuentos del marketplace' };
  if (pathname.startsWith('/admin/facturacion')) return { titulo: 'Facturación', sub: 'Sube el PDF y XML de las facturas solicitadas por los clientes' };
  if (pathname.startsWith('/admin/configuracion')) return { titulo: 'Configuración Global', sub: 'Ajustes del sistema' };
  return { titulo: 'Panel', sub: '' };
};

const iniciales = (nombre = '') =>
  nombre.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || 'AD';

const AdminLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = useSelector(state => state.adminAuth.admin);

  const [badges, setBadges] = useState({ verificacion: 0, tickets: 0, soporte: 0 });
  // Ya llegó un total de soporte por socket: es más reciente que lo que traiga una respuesta de stats
  const soporteEnVivo = useRef(false);

  const cargarBadges = useCallback(() => {
    adminAxios.get('/api/admin/stats')
      .then(res => setBadges(prev => ({
        verificacion: res.data.verificacionesPendientes || 0,
        tickets: res.data.ticketsPendientes || 0,
        soporte: soporteEnVivo.current ? prev.soporte : (res.data.soporteSinLeer || 0),
      })))
      .catch(() => {});
  }, []);

  // Mensajes de soporte sin leer en vivo (sala 'admins'): el servidor manda el total ya sumado
  // al llegar un mensaje (support_message) y al leerse una conversación (support_unread).
  useEffect(() => {
    cargarBadges();
    const socket = conectarSocketAdmin();
    let yaConecto = false;
    socket.on('connect', () => {
      socket.emit('join_admin');
      // Al reconectar pudimos perdernos avisos: se vuelve a sincronizar con el servidor
      if (yaConecto) { soporteEnVivo.current = false; cargarBadges(); }
      yaConecto = true;
    });
    const fijarSoporte = (data) => {
      if (typeof data?.totalSinLeer !== 'number') return;
      soporteEnVivo.current = true;
      setBadges(prev => ({ ...prev, soporte: data.totalSinLeer }));
    };
    socket.on('support_message', fijarSoporte);
    socket.on('support_unread', fijarSoporte);
    return () => { socket.disconnect(); };
  }, [cargarBadges]);

  const nombre = admin?.name || 'Administrador';
  const { titulo, sub } = metaPorRuta(location.pathname, nombre);
  const hoy = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });

  const salir = () => {
    dispatch(logoutAdmin());
    navigate('/admin/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* ---------- Sidebar ---------- */}
      <Box
        component="nav"
        sx={{
          width: ANCHO, flexShrink: 0, bgcolor: 'white', borderRight: 1, borderColor: 'sutil',
          display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 3 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', color: 'primary.main', width: 36, height: 36 }}> </Avatar>
          <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>One Service Adm</Typography>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2 }}>
          {secciones.map((sec) => (
            <Box key={sec.titulo} sx={{ mb: 1.5 }}>
              <Typography sx={{ px: 1.5, py: 1, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'text.secondary', textTransform: 'uppercase' }}>
                {sec.titulo}
              </Typography>
              <List disablePadding>
                {sec.items.map((item) => (
                  <ListItemButton
                    key={item.to}
                    component={NavLink}
                    to={item.to}
                    end={item.end}
                    sx={{
                      borderRadius: 2, mb: 0.5, py: 1, color: 'text.secondary',
                      '& .MuiListItemIcon-root': { color: 'text.secondary', minWidth: 38 },
                      '&.active': {
                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'primary.main', fontWeight: 600,
                        '& .MuiListItemIcon-root': { color: 'primary.main' },
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
                    {item.badge && badges[item.badge] > 0 && (
                      <Chip label={badges[item.badge] > 99 ? '99+' : badges[item.badge]} size="small"
                        sx={{ height: 20, bgcolor: 'success.claro', color: 'primary.main', fontWeight: 700 }} />
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, m: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Avatar sx={{ bgcolor: 'secondary.main', color: 'primary.main', width: 38, height: 38, fontSize: 14 }}>{iniciales(nombre)}</Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: 'primary.main' }}>{nombre}</Typography>
            <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{admin?.email || ''}</Typography>
          </Box>
          <Tooltip title="Cerrar sesión">
            <IconButton onClick={salir} size="small"><LogoutOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ---------- Contenido ---------- */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4, py: 3, gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{titulo}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{sub}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip icon={<CalendarTodayOutlinedIcon />} label={hoy}
              sx={{ bgcolor: 'white', border: 1, borderColor: 'sutil', fontWeight: 500, '& .MuiChip-icon': { color: 'text.secondary' } }} />
            <AdminNotifications />
            <Avatar sx={{ bgcolor: 'secondary.main', color: 'primary.main', width: 40, height: 40, fontSize: 14 }}>{iniciales(nombre)}</Avatar>
          </Box>
        </Box>

        <Box sx={{ px: 4, pb: 4 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
